import { useEffect, type ReactNode } from "react";
import { config, BRAIN_SUPPLY_CAP } from "../config";
import { shortAddr } from "../lib/format";
import { Notice } from "../components/ui";

/**
 * Documentation — single deep-linkable page (#/docs/<section>) following the sectioning
 * convention of major protocol docs (Uniswap, Aave, Lido): Introduction -> Getting started ->
 * Core concepts -> per-feature Guides -> protocol Reference -> FAQ -> Resources.
 */

interface TocEntry {
  id: string;
  label: string;
}
interface TocGroup {
  group: string;
  entries: TocEntry[];
}

const TOC: TocGroup[] = [
  {
    group: "Introduction",
    entries: [
      { id: "overview", label: "Overview" },
      { id: "getting-started", label: "Getting started" },
      { id: "concepts", label: "Core concepts" },
    ],
  },
  {
    group: "Guides",
    entries: [
      { id: "inference", label: "Run inference" },
      { id: "agent-access", label: "Agent / API access" },
      { id: "account", label: "Spending account" },
      { id: "operate", label: "Operate a Brain" },
      { id: "stake", label: "Stake & earn" },
      { id: "govern", label: "Governance" },
      { id: "bridge", label: "Bridge a Brain" },
    ],
  },
  {
    group: "Reference",
    entries: [
      { id: "payments", label: "Payment protocol" },
      { id: "parameters", label: "Protocol parameters" },
      { id: "faq", label: "FAQ" },
      { id: "resources", label: "Contracts & links" },
    ],
  },
];

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={`doc-${id}`} className="doc-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function AddrRow({ label, addr, explorer }: { label: string; addr: string; explorer: string }) {
  const unset = /^0x0{40}$/i.test(addr);
  return (
    <tr>
      <td>{label}</td>
      <td>
        {unset ? (
          <span className="muted">not configured</span>
        ) : (
          <a className="mono" href={`${explorer}/address/${addr}`} target="_blank" rel="noreferrer">
            {shortAddr(addr as `0x${string}`)}
          </a>
        )}
      </td>
    </tr>
  );
}

export function Docs({ route }: { route: string }) {
  const section = route.startsWith("/docs/") ? route.slice("/docs/".length) : "";

  useEffect(() => {
    if (!section) {
      window.scrollTo({ top: 0 });
      return;
    }
    document.getElementById(`doc-${section}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [section]);

  const l2 = config.l2;
  const l1 = config.l1;

  return (
    <>
      <div className="section-title">Documentation</div>
      <div className="docs">
        <nav className="docs-toc">
          {TOC.map((g) => (
            <div key={g.group}>
              <div className="group">{g.group}</div>
              {g.entries.map((e) => (
                <a key={e.id} href={`#/docs/${e.id}`} className={section === e.id ? "active" : ""}>
                  {e.label}
                </a>
              ))}
            </div>
          ))}
        </nav>

        <div className="docs-content">
          {/* ------------------------------------------------ Introduction */}
          <Section id="overview" title="Overview">
            <p className="doc-lede">
              basedAI is a decentralized LLM inference network on <strong>Ink</strong> (an Ethereum L2).
              <strong> Brains</strong> are staked NFTs, each running its own inference market; <strong>miners</strong> serve
              GPU inference on the Brain&apos;s model, <strong>validators</strong> score them, and users pay per request
              in <strong>$basedAI</strong>. There are no emissions and no new tokens — every participant is paid from
              real inference fees, split on-chain.
            </p>
            <p>
              This dApp is the network&apos;s cockpit. Everything it does is a thin, verifiable wrapper over the
              on-chain contracts and the gateway:
            </p>
            <ul>
              <li><strong>Brains</strong> — explore every Brain: status, model, miners, validators, stake, splits.</li>
              <li><strong>Inference</strong> — chat with a Brain (multi-turn) and pay only the metered cost per reply.</li>
              <li><strong>Account</strong> — fund and manage the spending balance that pays for inference.</li>
              <li><strong>Operate</strong> — activate and configure your Brain; register as a miner or validator.</li>
              <li><strong>Stake</strong> — delegate $basedAI to a validator and share its fee yield.</li>
              <li><strong>Govern</strong> — GigaBrain governance via a standard OpenZeppelin Governor (on Tally).</li>
              <li><strong>Bridge</strong> — move Brain NFTs between Ethereum (L1) and Ink (L2).</li>
            </ul>
            <p>
              The interface never trusts a miner or the gateway: responses are hash-verified in your browser and
              charges are re-derived independently before you authorize payment (see{" "}
              <a href="#/docs/payments">Payment protocol</a>).
            </p>
          </Section>

          <Section id="getting-started" title="Getting started">
            <h3>1. Install a wallet</h3>
            <p>
              You need an injected, MetaMask-compatible browser wallet (any EIP-1193 provider). Click{" "}
              <strong>Connect Wallet</strong> in the top bar; the app reconnects automatically on later visits once
              authorized.
            </p>
            <h3>2. Get on the right network</h3>
            <p>
              The dApp lives on <strong>{l2.name}</strong> (chain ID <span className="mono">{l2.chainId}</span>). If
              your wallet is on another chain a warning banner appears with a one-click{" "}
              <strong>Switch network</strong> link that adds the chain to your wallet if it is missing. Gas is paid in{" "}
              {l2.nativeSymbol}. Minting and bridging Brains happen on <strong>{l1.name}</strong> (L1) — the wallet
              will prompt you to switch when needed.
            </p>
            <h3>3. Fund your spending account</h3>
            <p>
              Inference is paid from an escrowed <strong>spending balance</strong>, not straight from your wallet. Get
              $basedAI on L2, then on the <a href="#/account">Account</a> page deposit into the market contract. Two
              wallet confirmations: an ERC-20 approval (only when needed) and the deposit itself.
            </p>
            <h3>4. Run your first inference</h3>
            <p>
              On the <a href="#/inference">Inference</a> page, keep the default Brain, type a prompt, set a small
              budget (the default <span className="mono">0.05</span> is plenty for a short reply), and press{" "}
              <strong>Send prompt</strong>. You will sign one message before the request (the bounded
              pre-authorization) and one after (the exact final charge).
            </p>
            <Notice>
              <strong>Preview mode.</strong> Until contract addresses are configured (via{" "}
              <span className="mono">VITE_*</span> env vars or <span className="mono">window.__BASEDAI_CONFIG__</span>),
              the app shows a banner and on-chain reads/writes fail. The interface stays fully navigable so you can
              explore every surface.
            </Notice>
          </Section>

          <Section id="concepts" title="Core concepts">
            <table className="doc-table">
              <thead>
                <tr>
                  <th>Concept</th>
                  <th>What it is</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Brain</td>
                  <td>
                    One of {BRAIN_SUPPLY_CAP} NFTs (IDs 0–{BRAIN_SUPPLY_CAP - 1}) minted on Ethereum by staking
                    Pepecoin or $basedAI. Once bridged to L2, each Brain runs its own inference market: a model
                    URI (e.g. a Hugging Face link), a set of registered miners and validators, and a fee split,
                    all activated and configured by its owner. IDs 0–6 are reserved for admin/governance seeding;
                    public Brains start at 7.
                  </td>
                </tr>
                <tr>
                  <td>Miner</td>
                  <td>
                    A GPU node serving a Brain&apos;s model (reference implementation:{" "}
                    <span className="mono">basedai-miner</span>). Paid per request from user fees.
                  </td>
                </tr>
                <tr>
                  <td>Validator</td>
                  <td>
                    A node that scores miners&apos; quality (reference:{" "}
                    <span className="mono">basedai-validator</span>). Earns the validator share of fees; needs
                    delegated stake to carry quorum weight.
                  </td>
                </tr>
                <tr>
                  <td>$basedAI</td>
                  <td>
                    The fixed-supply ERC-20 fee token. Used for the spending account, staking, and registration fees.
                    No emissions — all yield is real fee income.
                  </td>
                </tr>
                <tr>
                  <td>Gateway</td>
                  <td>
                    An HTTP service that discovers miners and proxies inference requests to the highest-scored one.
                    Untrusted: everything it returns is verified client-side.
                  </td>
                </tr>
                <tr>
                  <td>Receipt</td>
                  <td>
                    A signed payment authorization binding you, the miner, the Brain, the prompt/response hashes, an
                    amount, an expiry, and a nonce. The only thing that lets a miner draw from your balance.
                  </td>
                </tr>
              </tbody>
            </table>
          </Section>

          {/* ------------------------------------------------ Guides */}
          <Section id="inference" title="Guide — Run inference">
            <p className="doc-lede">
              Send a prompt to any active Brain and pay only the metered cost of the delivered response.
            </p>
            <h3>Steps</h3>
            <ol>
              <li>
                <strong>Pick a Brain</strong> — browse the <a href="#/brains">explorer</a> (status, model, miners,
                validators, stake per Brain) or enter an ID directly (0–{BRAIN_SUPPLY_CAP - 1}; public Brains start
                at 7). An inactive Brain or zero announced miners means requests will fail — pick another.
              </li>
              <li>
                <strong>Chat.</strong> The Inference page is a multi-turn conversation: each message sends the whole
                transcript (speaker-labelled, the same convention the SDK sidecar uses), and each reply shows the
                serving miner, its score-based routing, and the exact charge.
              </li>
              <li>
                <strong>Set a budget.</strong> This is a ceiling, not a price: the final charge is metered from actual
                bytes and can only be lower. If your spending balance is below the budget a warning appears — top up
                on the <a href="#/account">Account</a> page.
              </li>
              <li>
                <strong>Send</strong> and sign the pre-authorization in your wallet. The request routes through the
                gateway to the highest-scored miner.
              </li>
              <li>
                <strong>Counter-sign the final receipt</strong> once the response arrives and verifies. The response
                card shows the delivered text, the exact charge, and token counts.
              </li>
            </ol>
            <h3>Reading the result</h3>
            <ul>
              <li>
                <strong>Settled</strong> badge — the exact metered charge was counter-signed and delivered to the
                miner. That amount is what you pay.
              </li>
              <li>
                <strong>Pre-auth only</strong> badge — settlement did not complete; the miner can redeem at most the
                bounded pre-authorization (never more than your budget or the market&apos;s reservation cap).
              </li>
            </ul>
            <h3>Safety checks made for you</h3>
            <p>
              Before authorizing payment the app verifies that the delivered text hashes to the committed response
              hash, that the final receipt binds that exact response, and that the charge equals the price re-derived
              from byte counts. Any mismatch aborts with no full payment authorized — see{" "}
              <a href="#/docs/payments">Payment protocol</a>.
            </p>
          </Section>

          <Section id="agent-access" title="Guide — Agent / API access">
            <p className="doc-lede">
              Use a Brain from your own agent, script, or app. BasedAI has no shared-secret API keys — the
              credential is a <strong>session key</strong>: a throwaway wallet whose spending balance is its
              entire budget.
            </p>
            <h3>Why not classic API keys?</h3>
            <ul>
              <li>
                Payment is authorized by a <strong>signature on every receipt</strong>; the gateway is a
                stateless, untrusted proxy with no accounts to attach a key to. A bearer token cannot sign
                receipts — only a key can.
              </li>
              <li>
                A leaked API key is unbounded liability until a server revokes it. A leaked session key can
                lose at most its own spending balance; per-request exposure is additionally capped by the
                market&apos;s <span className="mono">maxReservation</span>.
              </li>
              <li>
                &quot;Revocation&quot; is self-custodial: withdraw the balance (1-day delay) or simply stop
                topping it up.
              </li>
            </ul>
            <h3>Set up (once)</h3>
            <ol>
              <li>
                On the <a href="#/api">API</a> page, <strong>generate a session key</strong> — it is created in
                your browser and never transmitted. Save the private key immediately.
              </li>
              <li>
                <strong>Fund it</strong> from your main wallet. Funding uses the market&apos;s{" "}
                <span className="mono">depositFor</span>, which credits the session key&apos;s spending balance
                directly — the key itself <strong>never needs ETH</strong>: inference only signs off-chain
                receipts, and miners submit (and pay for) settlement.
              </li>
              <li>
                <strong>Copy the environment block</strong> the page generates (key, RPC, contract addresses,
                gateway URL) into your agent&apos;s environment.
              </li>
            </ol>
            <h3>Connect your agent</h3>
            <ul>
              <li>
                <strong>OpenAI-compatible sidecar</strong> (recommended): <span className="mono">pip install
                basedai-client</span>, then run <span className="mono">basedai-sidecar</span>. Point any OpenAI
                client or agent framework at <span className="mono">http://127.0.0.1:8402/v1</span> with model{" "}
                <span className="mono">brain-&lt;id&gt;</span>; the api_key field is ignored. The sidecar runs
                locally because signing must stay where the key is — it performs the full verify-and-meter
                protocol on every call.
              </li>
              <li>
                <strong>Direct SDKs</strong>: <span className="mono">basedai_client</span> (Python) and{" "}
                <span className="mono">client/typescript</span> expose <span className="mono">infer()</span>{" "}
                with explicit budgets, plus deposit/withdraw helpers.
              </li>
            </ul>
            <Notice kind="warn">
              Treat the session key like cash: fund it with small amounts, per agent, and top up as needed.
              Never reuse your main wallet&apos;s key in an agent.
            </Notice>
          </Section>

          <Section id="account" title="Guide — Spending account">
            <p className="doc-lede">
              The spending account is escrowed $basedAI in the market contract that pays for inference.
            </p>
            <h3>Deposit</h3>
            <ol>
              <li>Enter an amount on the <a href="#/account">Account</a> page and press <strong>Approve &amp; deposit</strong>.</li>
              <li>
                Confirm the ERC-20 approval if prompted (skipped when the existing allowance suffices), then the
                deposit transaction. Your <strong>Spending balance</strong> updates immediately.
              </li>
            </ol>
            <h3>Withdraw (two steps, 1-day delay)</h3>
            <ol>
              <li><strong>Request</strong> — enter an amount and press <strong>Request</strong>. A pending withdrawal starts the safety clock.</li>
              <li><strong>Withdraw</strong> — after the 1-day delay the <strong>Withdraw</strong> button unlocks and the banner shows the exact ready time.</li>
            </ol>
            <Notice kind="warn">
              The delay exists so miners can redeem receipts you already signed before your funds exit — signed
              receipts take priority over your withdrawal. Never sign receipts you don&apos;t intend to honor.
            </Notice>
          </Section>

          <Section id="operate" title="Guide — Operate a Brain">
            <p className="doc-lede">
              Own a Brain? Bring it online, choose the model, set the fee split. Anyone can register as a miner
              or validator on an active Brain.
            </p>
            <h3>Owner actions</h3>
            <p>
              You must hold the (bridged) Brain NFT on L2 — see <a href="#/docs/bridge">Bridge a Brain</a>. Enter your
              Brain ID on the <a href="#/operate">Operate</a> page; the card confirms you as owner.
            </p>
            <ul>
              <li>
                <strong>Activate Brain</strong> — set the model URI (e.g. an IPFS or Hugging Face URL). The model
                hash is committed on-chain as <span className="mono">keccak256(modelURI)</span>.
              </li>
              <li>
                <strong>Update model</strong> — change the model URI of an already-active Brain at any time.
              </li>
              <li>
                <strong>Fee split</strong> — two knobs, in basis points:
                <ul>
                  <li><strong>Owner split</strong>: your cut of gross fees, capped at 1500 bps (15%).</li>
                  <li>
                    <strong>Miner share</strong>: of the remaining node share, how much goes to the serving miner;
                    the rest goes to validators.
                  </li>
                </ul>
              </li>
            </ul>
            <Notice kind="warn">
              While a Brain has zero validators the validator share folds to the miner. Register a validator to
              enable delegated staking yield on your Brain.
            </Notice>
            <h3>Join as a miner</h3>
            <ol>
              <li>Press <strong>Register miner</strong> — registration burns the Brain&apos;s registration fee in $basedAI (approve if prompted).</li>
              <li>Run the reference miner (<span className="mono">basedai-miner</span>) pointed at this Brain, serving its model.</li>
              <li><strong>Deregister</strong> anytime to stop appearing in miner discovery.</li>
            </ol>
            <h3>Join as a validator</h3>
            <ol>
              <li>Press <strong>Register validator</strong> (same burned registration fee).</li>
              <li>Run <span className="mono">basedai-validator</span> to score miners.</li>
              <li>
                Attract delegated stake (see <a href="#/docs/stake">Stake &amp; earn</a>) — quorum weight and fee
                share scale with stake.
              </li>
            </ol>
          </Section>

          <Section id="stake" title="Guide — Stake & earn">
            <p className="doc-lede">
              Delegate $basedAI to a <strong>(Brain, validator)</strong> pool and earn a pro-rata share of that
              validator&apos;s fee income. Yield is real fees — there are no emissions.
            </p>
            <h3>Stake</h3>
            <ol>
              <li>On the <a href="#/stake">Stake</a> page enter the Brain ID and the validator&apos;s address.</li>
              <li>
                The position card shows your active stake, anything in cooldown, and the validator&apos;s total stake
                (its quorum weight).
              </li>
              <li>Enter an amount and press <strong>Approve &amp; stake</strong> (approval only when needed).</li>
            </ol>
            <h3>Unstake (14-day cooldown)</h3>
            <ol>
              <li><strong>Request unstake</strong> — moves the amount from active stake into cooldown.</li>
              <li><strong>Claim</strong> — after 14 days, returns the tokens to your wallet.</li>
            </ol>
            <Notice kind="warn">
              Stake in cooldown remains <strong>slashable</strong> until claimed. Delegating is vouching: if your
              validator misbehaves, your stake shares the penalty — pick validators you trust.
            </Notice>
          </Section>

          <Section id="govern" title="Guide — Governance">
            <p className="doc-lede">
              GigaBrain governance: one vote per Brain whose stake crosses the GigaBrain threshold.
            </p>
            <ul>
              <li>
                <strong>BasedGovernor</strong> is a standard OpenZeppelin Governor behind a timelock. Voting power is
                snapshotted at proposal time, each Brain votes at most once, and passed proposals execute only after
                the timelock delay.
              </li>
              <li>
                Because it is a stock Governor, propose, delegate, and vote through <strong>Tally</strong> — the{" "}
                <a href="#/govern">Govern</a> page links straight to it and shows the governor and timelock addresses.
              </li>
              <li>
                Governance has no voting power until Brains are bridged to L2; the reserved admin Brains (0–6) seed it.
              </li>
            </ul>
          </Section>

          <Section id="bridge" title="Guide — Bridge a Brain">
            <p className="doc-lede">
              Brains are minted on Ethereum (L1) by staking Pepecoin or $basedAI; they must be bridged to Ink (L2) to
              operate, stake, or vote.
            </p>
            <ol>
              <li>
                <strong>Mint on L1</strong> — stake Pepecoin or $basedAI into the Brain NFT contract on {l1.name}.
                Stake-minted Brains are <strong>soulbound</strong>: non-transferable except into and out of the
                authorized bridge.
              </li>
              <li>
                <strong>Deposit to L2</strong> — use Ink&apos;s canonical ERC-721 bridge (linked from the{" "}
                <a href="#/bridge">Bridge</a> page). The optional <span className="mono">BrainBridgeAdapter</span>{" "}
                wraps approval + deposit into one call.
              </li>
              <li>
                <strong>Withdraw back to L1</strong> — the standard Ink withdrawal flow burns the L2 Brain and
                releases the L1 escrow after the challenge window.
              </li>
            </ol>
            <Notice kind="warn">
              Minting and bridging prompt your wallet to switch to {l1.name}. The rest of the dApp runs on {l2.name}.
            </Notice>
          </Section>

          {/* ------------------------------------------------ Reference */}
          <Section id="payments" title="Reference — Payment protocol">
            <p className="doc-lede">
              The payment path is designed so that <strong>no honest user can be overcharged and no honest miner can
              go unpaid</strong>, without trusting the gateway or each other.
            </p>
            <ol>
              <li>
                <strong>Pre-authorization.</strong> Your browser signs a receipt whose{" "}
                <span className="mono">responseHash</span> is a deterministic sentinel (no response exists yet) and
                whose amount is bounded: at most your budget, and never more than the market&apos;s on-chain{" "}
                <span className="mono">maxReservation</span> cap. It expires after 1 hour and carries a random nonce,
                so it cannot be replayed or hoarded.
              </li>
              <li>
                <strong>Routing.</strong> The gateway forwards prompt + pre-auth to the highest-scored announced miner
                for that Brain.
              </li>
              <li>
                <strong>Verification.</strong> When the response returns, the app checks that the delivered text
                hashes (keccak256) to the committed response hash, and that the proposed final receipt matches the
                pre-auth&apos;s identity fields (user, miner, Brain, prompt hash, expiry, nonce) and binds the
                delivered response.
              </li>
              <li>
                <strong>Independent metering.</strong> The charge is re-derived in your browser from on-chain pricing
                and UTF-8 byte counts:
                <div className="codeblock" style={{ margin: "10px 0" }}>
                  charge = min(pricePerRequest + pricePerByte × (promptBytes + responseBytes), budget)
                </div>
                A final receipt with any other amount is rejected.
              </li>
              <li>
                <strong>Counter-signature &amp; settlement.</strong> Only your signature over the final receipt
                authorizes the exact cost. Settlement is best-effort: if it fails, the miner&apos;s fallback is the
                bounded pre-auth, never more.
              </li>
            </ol>
            <p>
              Receipts are signed as EIP-191 messages over a keccak256 digest of the ABI-encoded fields (market
              address, chain ID, user, miner, Brain ID, prompt hash, response hash, amount, expiry, nonce) — exactly
              what the market contract verifies on redemption.
            </p>
          </Section>

          <Section id="parameters" title="Reference — Protocol parameters">
            <table className="doc-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Value</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Brain supply</td>
                  <td>{BRAIN_SUPPLY_CAP}</td>
                  <td>IDs 0–{BRAIN_SUPPLY_CAP - 1}; 0–6 reserved, public from 7.</td>
                </tr>
                <tr>
                  <td>Withdrawal delay</td>
                  <td>1 day</td>
                  <td>Spending-account exits; signed receipts redeem first.</td>
                </tr>
                <tr>
                  <td>Unstake cooldown</td>
                  <td>14 days</td>
                  <td>Stake stays slashable until claimed.</td>
                </tr>
                <tr>
                  <td>Owner split cap</td>
                  <td>1500 bps (15%)</td>
                  <td>Maximum Brain-owner cut of gross fees.</td>
                </tr>
                <tr>
                  <td>Pre-auth expiry</td>
                  <td>1 hour</td>
                  <td>Signed pre-authorizations lapse if unused.</td>
                </tr>
                <tr>
                  <td>Reservation cap</td>
                  <td>on-chain <span className="mono">maxReservation</span></td>
                  <td>Upper bound on any pre-auth, whatever your budget.</td>
                </tr>
                <tr>
                  <td>Generation defaults</td>
                  <td>256 tokens, temp 0.7</td>
                  <td>Request defaults sent by this interface.</td>
                </tr>
                <tr>
                  <td>Registration fee</td>
                  <td>per-Brain</td>
                  <td>Burned on miner/validator registration; shown on Operate.</td>
                </tr>
              </tbody>
            </table>
          </Section>

          <Section id="faq" title="FAQ">
            <h3>Why did I pay less than my budget?</h3>
            <p>
              The budget is only a ceiling. The actual charge is metered from the request fee plus per-byte pricing on
              the prompt and response — usually far below the ceiling.
            </p>
            <h3>What if a miner returns garbage or lies about the output?</h3>
            <p>
              The response is hash-verified in your browser before the final receipt is signed. If the text does not
              match the committed hash, or the receipt does not bind that exact response, the app refuses to authorize
              full payment; the miner&apos;s recourse is capped at the bounded pre-authorization.
            </p>
            <h3>Why is there a withdrawal delay on the spending account?</h3>
            <p>
              So miners holding receipts you signed can redeem them before funds exit. Without it, a user could
              consume inference and immediately withdraw the balance backing the payment.
            </p>
            <h3>Why does unstaking take 14 days?</h3>
            <p>
              The cooldown keeps stake accountable: misbehavior discovered shortly after the fact can still be slashed
              before the stake escapes.
            </p>
            <h3>Where does yield come from?</h3>
            <p>
              Entirely from inference fees. $basedAI is fixed-supply; nothing is minted. Staking yield is your
              pro-rata share of your validator&apos;s fee income.
            </p>
            <h3>The app says &quot;Preview mode&quot; — is it broken?</h3>
            <p>
              No — contract addresses just aren&apos;t configured yet (pre-deployment). Every page is navigable;
              on-chain reads and writes activate once addresses are set.
            </p>
            <h3>Can I get an API key for my agent?</h3>
            <p>
              Yes — in the form of a session key: a fresh wallet you generate on the <a href="#/api">API</a>{" "}
              page and fund with a bounded budget. See <a href="#/docs/agent-access">Agent / API access</a> for
              why this is strictly safer than a shared-secret key.
            </p>
            <h3>Which wallets work?</h3>
            <p>
              Any injected EIP-1193 wallet (MetaMask or compatible). Signing uses standard{" "}
              <span className="mono">personal_sign</span> messages and normal transactions.
            </p>
            <h3>What do transactions cost?</h3>
            <p>
              Gas on {l2.name} is paid in {l2.nativeSymbol} at L2 prices; inference itself is paid in $basedAI from
              your spending balance.
            </p>
          </Section>

          <Section id="resources" title="Contracts & links">
            <h3>L2 — {l2.name} (chain {l2.chainId})</h3>
            <table className="doc-table">
              <tbody>
                <AddrRow label="$basedAI token" addr={l2.contracts.based} explorer={l2.explorer} />
                <AddrRow label="Brain registry (SubnetRegistry)" addr={l2.contracts.subnetRegistry} explorer={l2.explorer} />
                <AddrRow label="Compute market" addr={l2.contracts.market} explorer={l2.explorer} />
                <AddrRow label="Staking vault" addr={l2.contracts.stakingVault} explorer={l2.explorer} />
                <AddrRow label="Scoring registry" addr={l2.contracts.scoringRegistry} explorer={l2.explorer} />
                <AddrRow label="Brain NFT (L2)" addr={l2.contracts.brainNFTL2} explorer={l2.explorer} />
                <AddrRow label="Governor" addr={l2.contracts.governor} explorer={l2.explorer} />
                <AddrRow label="Timelock" addr={l2.contracts.timelock} explorer={l2.explorer} />
              </tbody>
            </table>
            <h3>L1 — {l1.name} (chain {l1.chainId})</h3>
            <table className="doc-table">
              <tbody>
                <AddrRow label="Brain NFT (L1)" addr={l1.contracts.brainNFT} explorer={l1.explorer} />
                <AddrRow label="Bridge adapter" addr={l1.contracts.bridgeAdapter} explorer={l1.explorer} />
                <AddrRow label="Canonical ERC-721 bridge" addr={l1.contracts.l1Erc721Bridge} explorer={l1.explorer} />
              </tbody>
            </table>
            <h3>Links</h3>
            <ul>
              <li><a href="#/whitepaper">Whitepaper v2</a> — the full protocol design</li>
              <li><a href={l2.explorer} target="_blank" rel="noreferrer">L2 explorer</a> · <a href={l1.explorer} target="_blank" rel="noreferrer">L1 explorer</a></li>
              <li><a href={config.governanceUrl} target="_blank" rel="noreferrer">Governance on Tally</a></li>
              <li><a href={config.bridgeUrl} target="_blank" rel="noreferrer">Ink canonical bridge</a></li>
              <li>Node software: <span className="mono">basedai-miner</span>, <span className="mono">basedai-validator</span> (see the repository)</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
}
